import {
  type ClientMessage,
  PING_FRAME,
  PROTOCOL_VERSION,
  type ServerMessage,
  socketProtocols,
} from "@app/protocol";
import { API_BASE } from "../config";
import { CHECK_EVERY_MS, heard, type Liveness, nextAction, pinged } from "./liveness";
import { afterClose, FRESH, opened, type Retry, read } from "./reconnect";
import { socketUrl } from "./socketUrl";

export type Connection = "connecting" | "open" | "closed";

/** `Omit` over a union must distribute, or the discriminant is lost. */
export type ClientBody = ClientMessage extends infer M
  ? M extends unknown
    ? Omit<M, "v">
    : never
  : never;

export interface SocketPorts {
  onMessage(message: ServerMessage): void;
  onConnection(state: Connection): void;
  /** The server speaks a protocol this build cannot read: only a reload helps (review 0027 C1). */
  onStale(): void;
  /** Several failures in a row: the session may have run out, or the server is away (C2, C3). */
  onUnreachable(): void;
  /** The table will not have us; the socket has stopped for good (review 0027 D7). */
  onRefused(): void;
}

/** Reconnecting WebSocket for one table. Backs off 1 s → 8 s; the server restores the seat (review 0005 S2). */
export class TableSocket {
  private ws: WebSocket | null = null;
  private retry: Retry = FRESH;
  private stopped = false;
  private live: Liveness = { lastHeardAt: 0, pingedAt: null };
  private watchdog: number | null = null;
  private readonly url: string;
  /** The session rides here, not in the URL (review 0019 U8). */
  private readonly protocols: string[];
  private readonly ports: SocketPorts;

  /**
   * Back from the background (a channel switch, a locked phone): timers were
   * throttled meanwhile, so ask for the verdict at once instead of waiting a tick.
   */
  private readonly onVisible = (): void => {
    if (document.visibilityState === "visible") this.check();
  };

  constructor(
    instanceId: string,
    session: string,
    ports: SocketPorts,
    guildId: string | null = null,
  ) {
    this.url = socketUrl({
      protocol: window.location.protocol,
      host: window.location.host,
      search: window.location.search,
      instanceId,
      guildId,
      base: API_BASE,
    });
    this.protocols = socketProtocols(session);
    this.ports = ports;
    document.addEventListener("visibilitychange", this.onVisible);
    this.connect();
  }

  /** The socket says nothing while it is dead; only the clock can tell us (see `liveness`). */
  private check(): void {
    const ws = this.ws;
    if (ws === null || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    const action = nextAction(this.live, now);
    if (action === "ping") {
      this.live = pinged(this.live, now);
      // The exact text: the table answers it without waking up (review 0027 D4).
      ws.send(PING_FRAME);
      return;
    }
    // Nothing answered the prod. A dead socket may take long to say it closed: let go of it now.
    if (action === "dead") this.drop(ws);
  }

  private stopWatchdog(): void {
    if (this.watchdog !== null) window.clearInterval(this.watchdog);
    this.watchdog = null;
  }

  /** Stops listening to a socket and closes it, then carries on as if it had reported the close. */
  private drop(ws: WebSocket): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.close();
    this.closed(1006);
  }

  private closed(code: number): void {
    this.ws = null;
    this.stopWatchdog();
    // A socket we closed ourselves (moving to another table) reports nothing.
    if (this.stopped) return;
    const next = afterClose(this.retry, code, Date.now());
    this.retry = next.retry;
    if (next.refused) {
      this.stopped = true;
      this.ports.onRefused();
      return;
    }
    this.ports.onConnection(next.state);
    if (next.unreachable) this.ports.onUnreachable();
    window.setTimeout(() => this.connect(), next.delay);
  }

  private hear(data: string): void {
    // A socket we let go of may still deliver a frame; it belongs to a table we have left.
    if (this.stopped) return;
    this.live = heard(this.live, Date.now());
    const frame = read(data);
    if (frame.kind === "message") this.ports.onMessage(frame.message);
    if (frame.kind !== "stale") return;
    this.close();
    this.ports.onStale();
  }

  private connect(): void {
    if (this.stopped) return;
    this.ports.onConnection("connecting");
    const ws = new WebSocket(this.url, this.protocols);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = opened(this.retry);
      this.live = heard(this.live, Date.now());
      this.stopWatchdog();
      this.watchdog = window.setInterval(() => this.check(), CHECK_EVERY_MS);
      this.ports.onConnection("open");
    };
    ws.onmessage = (event) => this.hear(String(event.data));
    ws.onclose = (event) => this.closed(event.code);
  }

  send(message: ClientBody): void {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ v: PROTOCOL_VERSION, ...message }));
  }

  close(): void {
    this.stopped = true;
    this.stopWatchdog();
    document.removeEventListener("visibilitychange", this.onVisible);
    this.ws?.close();
  }
}
